import { getBudgetAmountInRange, hasBudgetAmountChangeBy } from './budgetAmounts';
/**
 * Utility functions for category and account operations
 */

/**
 * Finds an account by its ID or name (case-insensitive, partial match allowed for ID)
 * @param {Array} accounts - List of account objects
 * @param {string} accountId - The account ID or name to search for
 * @returns {Object|null} The found account object or null if not found
 */
export function findAccountById(accounts, accountId) {
  if (!accountId) return null;
  // First try to find by exact ID match
  let account = accounts.find(a => a.id === accountId);
  // If not found, try to find by name (case insensitive) or partial ID match
  if (!account) {
    account = accounts.find(a =>
      a.name.toLowerCase() === accountId.toLowerCase() ||
      a.id.toLowerCase().includes(accountId.toLowerCase()) ||
      accountId.toLowerCase().includes(a.id.toLowerCase())
    );
  }
  return account;
}

// Akahu (bank) account ids look like "acc_...". Historically this shape, stored
// in an account's `id`, was the only signal that the account was bank-linked.
const AKAHU_ID_PREFIX = 'acc_';

/**
 * Whether a value looks like an Akahu (bank) account id, e.g. "acc_...".
 * @param {*} value
 * @returns {boolean}
 */
export const isAkahuId = (value) => typeof value === 'string' && value.startsWith(AKAHU_ID_PREFIX);

/**
 * Returns the external (Akahu) account ids a local account is linked to.
 *
 * When `linkedAccounts` is present (an array), it is authoritative — even when
 * empty (an explicit "linked to nothing", e.g. after an unlink). Only when the
 * field is absent (never managed/migrated) do we fall back to deriving the link
 * from a legacy Akahu-format `id`.
 * @param {Object} account - An account object
 * @returns {string[]} De-duplicated list of linked external account ids
 */
export function getLinkedAccountIds(account) {
  if (!account) return [];
  if (Array.isArray(account.linkedAccounts)) {
    return account.linkedAccounts.filter(Boolean);
  }
  // Legacy/unmanaged: pre-migration, the Akahu id lived in `id`.
  return isAkahuId(account.id) ? [account.id] : [];
}

/**
 * Whether an account is linked to a bank (Akahu) account.
 * @param {Object} account - An account object
 * @returns {boolean}
 */
export function isAccountLinked(account) {
  return getLinkedAccountIds(account).length > 0;
}

/**
 * Whether the given external (Akahu) account id maps to this local account —
 * used when filing imported transactions against the right account.
 *
 * Matches on either the internal `id` or a linked external id. The internal-id
 * match is unconditional (it also covers legacy accounts whose `id` is itself an
 * acc_ value), which is correctness-first: failing to match here causes the
 * importer to create duplicate accounts. The consequence is that a legacy
 * account whose `id` is a bank id can't have that specific link removed by
 * unlinking alone — it would need its internal `id` re-keyed to a non-bank value
 * (and its transactions repointed) for unlink to fully take effect.
 * @param {Object} account - A local account object
 * @param {string} externalId - The external (Akahu) account id from an import
 * @returns {boolean}
 */
export function accountMatchesExternalId(account, externalId) {
  if (!account || !externalId) return false;
  if (account.id === externalId) return true;
  return getLinkedAccountIds(account).includes(externalId);
}

/**
 * How a category's money has actually been split across accounts recently.
 *
 * Used to attribute a budget that names no account ("All Accounts") when
 * forecasting. Splitting such a budget evenly across every account is the
 * obvious rule and the wrong one — it charges parking to the mortgage account
 * and groceries to the emergency fund. Where the category's own transactions
 * have been landing is a far better guess at where the next ones will.
 *
 * @param {Object} args
 * @param {string} args.categoryId
 * @param {string} [args.type='Expense'] - 'Expense' (outflows) or 'Income'
 * @param {Array} args.transactions
 * @param {number} [args.months=6] - lookback window, including this month
 * @param {Array<string>} [args.accountIds] - restrict to these accounts
 *   (e.g. the active ones); shares are renormalised over what survives
 * @param {Date} [args.today]
 * @returns {Object<string, number>|null} accountId -> share summing to 1, or
 *   null when the category has no matching transactions to learn from
 */
export function categoryAccountShares({
  categoryId,
  type = 'Expense',
  transactions = [],
  months = 6,
  accountIds,
  today = new Date(),
}) {
  const wantPositive = type === 'Income';
  const eligible = accountIds ? new Set(accountIds) : null;
  const start = new Date(today.getFullYear(), today.getMonth() - (months - 1), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const totals = {};
  let grandTotal = 0;

  transactions.forEach((t) => {
    if (!t || (t.category || 'uncategorized') !== (categoryId || 'uncategorized')) return;
    if (wantPositive ? !(t.amount > 0) : !(t.amount < 0)) return;
    if (eligible && !eligible.has(t.account)) return;
    const date = new Date(t.date);
    if (Number.isNaN(date.getTime()) || date < start || date > end) return;
    const amount = Math.abs(t.amount);
    totals[t.account] = (totals[t.account] || 0) + amount;
    grandTotal += amount;
  });

  if (!grandTotal) return null;
  Object.keys(totals).forEach((id) => { totals[id] /= grandTotal; });
  return totals;
}

/**
 * Gets the budget target for a category for a given month.
 * @param {Array} budgets - List of budget objects
 * @param {string} categoryId - The category ID
 * @param {boolean} isIncome - True if income, false if expense
 * @param {string} monthStr - Month string (e.g., 'Jan-25')
 * @param {string} selectedMonth - (Optional) fallback month string
 * @returns {number} The calculated budget target
 */
export function getBudgetTarget(budgets, categoryId, isIncome, monthStr, selectedMonth) {
  const categoryBudgets = budgets.filter(budget =>
    budget.categoryId === categoryId &&
    budget.type === (isIncome ? 'Income' : 'Expense') &&
    budget.isActive
  );
  if (categoryBudgets.length > 0) {
    const currentMonthStr = monthStr || selectedMonth;
    if (!currentMonthStr) return 0;
    const [monthAbbr, yearStr] = currentMonthStr.split('-');
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthAbbr);
    const year = 2000 + parseInt(yearStr, 10);
    const startOfMonth = new Date(year, monthIndex, 1);
    const endOfMonth = new Date(year, monthIndex + 1, 0);
    let totalBudget = 0;
    categoryBudgets.forEach(budget => {
      if (hasBudgetAmountChangeBy(budget, endOfMonth)) { totalBudget += getBudgetAmountInRange(budget, startOfMonth, endOfMonth); return; }
      const budgetStart = new Date(budget.startDate);
      const budgetEnd = budget.endDate ? new Date(budget.endDate) : new Date(2099, 11, 31);
      if (budgetStart <= endOfMonth && budgetEnd >= startOfMonth) {
        if (budget.isRecurring) {
          if (budget.isProrated) {
            const overlapStart = new Date(Math.max(budgetStart, startOfMonth));
            const overlapEnd = new Date(Math.min(budgetEnd, endOfMonth));
            const daysOverlap = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24) + 1;
            switch (budget.recurringPeriod) {
              case 'weekly':
                totalBudget += (budget.amount / 7) * daysOverlap; break;
              case 'monthly': {
                const daysInMonthLocal = (endOfMonth - startOfMonth) / (1000 * 60 * 60 * 24) + 1;
                totalBudget += budget.amount * (daysOverlap / daysInMonthLocal); break; }
              case 'quarterly':
                totalBudget += budget.amount * (daysOverlap / 91.25); break;
              case 'yearly':
                totalBudget += budget.amount * (daysOverlap / 365); break;
              default:
                totalBudget += budget.amount;
            }
          } else {
            switch (budget.recurringPeriod) {
              case 'monthly':
                totalBudget += budget.amount; break;
              case 'quarterly': {
                const startMonth = budgetStart.getMonth();
                const diff = (startOfMonth.getFullYear() - budgetStart.getFullYear()) * 12 + (startOfMonth.getMonth() - startMonth);
                if (diff >= 0 && diff % 3 === 0) totalBudget += budget.amount;
                break; }
              case 'yearly':
                totalBudget += budget.amount / 12; break;
              case 'weekly': {
                const targetDow = budgetStart.getDay();
                let count = 0;
                for (let d = new Date(startOfMonth); d <= endOfMonth; d.setDate(d.getDate() + 1)) {
                  if (d >= budgetStart && d <= budgetEnd && d.getDay() === targetDow) count++;
                }
                totalBudget += count * budget.amount; break; }
              default:
                totalBudget += budget.amount;
            }
          }
        } else {
          const daysInMonth = (endOfMonth - startOfMonth) / (1000 * 60 * 60 * 24) + 1;
          const overlapStart = new Date(Math.max(budgetStart, startOfMonth));
          const overlapEnd = new Date(Math.min(budgetEnd, endOfMonth));
          const daysOverlap = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24) + 1;
          totalBudget += Math.abs(budget.amount) * (daysOverlap / daysInMonth);
        }
      }
    });
    return totalBudget;
  }
  return 0;
}

/**
 * Gets a color for a category based on its index and type (income/expense).
 * @param {number} index - The index of the category
 * @param {string} type - 'income' or 'expense'
 * @param {object} theme - (Optional) MUI theme object for palette
 * @returns {string} The color string
 */
export function getCategoryColor(index, type, theme) {
  const baseColors = type === 'income'
    ? (theme ? [theme.palette.success.light, theme.palette.success.main, theme.palette.success.dark] : ['#81c784', '#388e3c', '#1b5e20'])
    : (theme ? [theme.palette.error.light, theme.palette.error.main, theme.palette.error.dark] : ['#e57373', '#d32f2f', '#b71c1c']);
  const colorIndex = index % baseColors.length;
  return baseColors[colorIndex];
} 
